import { Injectable } from '@angular/core'
import { BetOrder } from '../../../pages/my-bets/types/my-bets.type'
import { Runner } from '../../types/market.type'

@Injectable({
	providedIn: 'root',
})
export class AsianHandicapService {
	constructor() {}

	determineACHPnL(market: any, bets: any[]) {
		let combinedPnLs: any[] = []

		bets.forEach((bet) => {
			let conditionalPnLs = []
			const conditions = this.formulateConditions(market, bet)

			const isBack = bet?.side === 'BACK'
			const isWin = bet?.handicap < 0
			const hcpValues = bet?.handicapValues
			const isStakeSplit = hcpValues?.length === 2

			const betStake = Number(bet?.betSize)
			const stake = isStakeSplit ? betStake / 2 : betStake
			const calculatedPnL = (bet?.averagePriceMatched - 1) * stake
			const profit = isBack ? calculatedPnL : stake
			const loss = isBack ? stake : calculatedPnL

			conditionalPnLs = conditions?.map((condition) => {
				// If runner 1 wins by n goals
				let pnl = 0

				hcpValues?.forEach((value: any) => {
					const absHcpValue = Math.abs(value)
					if (isWin) {
						if (condition?.goals < absHcpValue) {
							pnl += isBack ? -loss : profit
						} else if (condition?.goals > absHcpValue) {
							pnl += isBack ? profit : -loss
						}
					} else {
						if (condition?.goals < absHcpValue) {
							pnl += isBack ? profit : -loss
						} else if (condition?.goals > absHcpValue) {
							pnl += isBack ? -loss : profit
						}
					}
				})
				return { ...condition, pnl }
			})
			combinedPnLs = [...combinedPnLs, ...conditionalPnLs]
		})
		const absGoals = combinedPnLs?.map((each) => ({
			...each,
			goals: Math.abs(each.goals),
		}))
		const aggregatePnL = this.aggregateACHPnL(market, absGoals)

		return this.optimizePnL(aggregatePnL)
	}

	aggregateACHPnL(market: any, data: any[]) {
		const map = new Map()

		data.forEach(({ selectionId, goals, pnl, runnerName }) => {
			// If we haven't seen this selectionId, initialize it
			if (!map.has(selectionId)) {
				map.set(selectionId, {
					selectionId,
					label: selectionId === 'Draw' ? 'Draw' : `If ${runnerName} win by`,
					pnls:
						selectionId === 'Draw'
							? [{ goals: 0, pnl: 0 }]
							: Array.from({ length: this.hcpRanges(market).pnlLength }, (_, i) => ({
									goals: i + 1,
									pnl: 0,
								})),
				})
			}
			// Add pnl to the corresponding goals index
			const entry = map.get(selectionId)
			if (selectionId === 'Draw') {
				entry.pnls[0].pnl += pnl
			} else {
				entry.pnls[goals - 1].pnl += pnl
			}
		})

		const result = Array.from(map.values())

		if (result.length > 0) {
			result[0].pnls.reverse()
		}

		return result
	}

	determineGLPnL(market: any, bets: any[]) {
		let combinedPnLs: any[] = []

		bets.forEach((bet: BetOrder) => {
			let conditionalPnLs = []
			const conditions = this.formulateConditions(market, bet, 'goal-lines')

			const isBack = bet?.side === 'BACK'
			const isOver = bet?.runnerName?.includes('Over')
			const hcpValues = bet?.handicapValues
			const isStakeSplit = hcpValues?.length === 2

			const betStake = Number(bet?.betSize)
			const stake = isStakeSplit ? betStake / 2 : betStake
			const calculatedPnL = (bet?.averagePriceMatched - 1) * stake
			const profit = isBack ? calculatedPnL : stake
			const loss = isBack ? stake : calculatedPnL

			conditionalPnLs = conditions?.map((condition) => {
				// If runner 1 wins by n goals
				let pnl = 0

				hcpValues?.forEach((value: any) => {
					const absHcpValue = Math.abs(value)
					if (isOver) {
						if (condition?.goals < absHcpValue) {
							pnl += isBack ? -loss : profit
						} else if (condition?.goals > absHcpValue) {
							pnl += isBack ? profit : -loss
						}
					} else {
						if (condition?.goals < absHcpValue) {
							pnl += isBack ? profit : -loss
						} else if (condition?.goals > absHcpValue) {
							pnl += isBack ? -loss : profit
						}
					}
				})
				return { ...condition, pnl }
			})
			combinedPnLs = [...combinedPnLs, ...conditionalPnLs]
		})
		const absGoals = combinedPnLs?.map((each) => ({
			...each,
			goals: Math.abs(each.goals),
		}))

		const aggregatePnL = this.aggregateGLPnL(market, absGoals)

		return this.optimizePnL(aggregatePnL)
	}

	aggregateGLPnL(market: any, data: any[]) {
		const map = new Map()
		const baseGoal = this.hcpRanges(market).baseGoal

		data.forEach(({ goals, pnl }) => {
			const key = market.marketId

			if (!map.has(key)) {
				map.set(key, {
					key,
					pnls: Array.from({ length: this.hcpRanges(market).pnlLength }, (_, i) => ({
						goals: i + baseGoal,
						pnl: 0,
					})),
				})
			}

			const entry = map.get(key)
			const goalIndex = goals - baseGoal
			if (entry?.pnls?.[goalIndex] != null) {
				entry.pnls[goalIndex].pnl += pnl
			}
		})

		return Array.from(map.values())
	}

	formulateConditions(market: any, placement: BetOrder, type: 'asian-handicap' | 'goal-lines' = 'asian-handicap') {
		const { selectionId, handicap } = placement || {}
		const isWin = handicap < 0
		const placementRunner = this.runners(market)?.find((runner) => runner?.selectionId === selectionId)
		const otherRunner = this.runners(market)?.find((runner) => runner?.selectionId !== selectionId)
		const runner = isWin ? placementRunner : otherRunner
		const negativeRunner = isWin ? otherRunner : placementRunner

		const conditions: any[] = []

		if (type === 'asian-handicap') {
			for (let i = this.hcpRanges(market).min; i <= this.hcpRanges(market).max; i++) {
				conditions.push({
					label:
						i === 0
							? 'Match Draws'
							: i < 0
								? `If ${negativeRunner?.runnerName} win by ${Math.abs(i)}`
								: `If ${runner?.runnerName} win by ${Math.abs(i)}`.trim(),
					goals: i,
					selectionId: i === 0 ? 'Draw' : i < 0 ? negativeRunner?.selectionId : runner?.selectionId,
					runnerName: i === 0 ? 'Draw' : i < 0 ? negativeRunner?.runnerName : runner?.runnerName,
				})
			}
		} else {
			for (let i = this.hcpRanges(market).min; i <= this.hcpRanges(market).max; i++) {
				conditions.push({ goals: i })
			}
		}

		return conditions
	}

	optimizePnL(data: any[]) {
		return data?.map((each) => {
			let filteredPnLs: any[] = []
			const currentPnLs = each?.pnls
			currentPnLs?.forEach((pnl: { goals: number; pnl: number }) => {
				const pnlExists = filteredPnLs?.[filteredPnLs?.length - 1]?.pnl === pnl?.pnl
				if (pnlExists) {
					filteredPnLs = filteredPnLs?.map((each) => {
						if (each?.pnl === pnl?.pnl) {
							return {
								...each,
								goals: Array.isArray(each?.goals) ? [...each.goals, pnl?.goals] : [each?.goals, pnl?.goals],
							}
						}

						return each
					})
				} else {
					filteredPnLs.push(pnl)
				}
			})

			return { ...each, pnls: filteredPnLs }
		})
	}

	hcpRanges(market: any) {
		const handicaps = market.runners?.map((runner: any) => runner.handicap)
		const minRange = Math.floor(Math.min(...handicaps))
		const maxRange = Math.ceil(Math.max(...handicaps))
		const isAsianHandicap = handicaps?.some((h: any) => h < 0)
		return {
			min: isAsianHandicap ? minRange - 1 : minRange,
			max: isAsianHandicap ? maxRange + 1 : maxRange,
			baseGoal: isAsianHandicap ? 0 : minRange,
			pnlLength: isAsianHandicap ? maxRange + 1 : maxRange - minRange + 1,
		}
	}

	runners(market: any): Runner[] {
		const runner1 = market?.runners?.[0] ?? {}
		const runner2 = market?.runners?.[1] ?? {}
		return [runner1, runner2]
	}
}
